import { IAirtableAttachment } from "@domain/CRM/Attachment";
import { BoundaryChangeType } from "./BoundaryChangeTypes";
import { JobFlags } from "./JobFlags";
import { JobsPriority } from "./JobsPriorities";
import { FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
import { BaseDataModel } from "@domain/CRM/BaseDataModel";
import type { Deal } from "@domain/CRM/deal";
import { defaultJobForImport, type JobImportDefaults } from "../operations/defaultJobForImport";
import { updateJobFromDeal } from "../operations/updateJobFromDeal";
import type { LatLonSource } from "../operations/countPlacedPullIns";

export enum JobStatusNumber {
	Unready = 0,
	Ready = 1,
	Sampled = 2,
	DropShipped = 3,
	CSVUnsent = 4,
	Completed = 7,
}

export interface ErrorNote {
	source: string;
	note: string;
}

/** One requirement the status calculator weighed on its way to the next status. */
export interface JobStatusCondition {
	condition: string;
	met: boolean;
}

/**
 * One of the three parallel chains the new status system advances independently.
 *
 * `conditions` are not the chain's whole vocabulary — they are the ones recorded by the
 * deepest check that ran. The calculator's ladder nests (a check runs only once the one
 * below it passed) and each check clears its predecessor's, so what survives is exactly
 * the set gating `next`. `next` is null at the end of a chain.
 *
 * `regressedFrom` is derived API-side rather than stored: the calculator records where a
 * chain has been but not that it went backwards, and the chain's option order — the only
 * way to tell "fell back" from "never got there" — lives in the Airtable package, which
 * is Python-only. See `API/src/infrastructure/airtable/mappers/job_status.py`.
 */
export interface JobStatusTrack {
	current?: string;
	currentDate?: string; // ISO — when `current` was set
	next?: string;
	conditions: JobStatusCondition[];
	/** The furthest status this chain reached, when it is no longer there. */
	regressedFrom?: string;
	regressedFromDate?: string; // ISO — when the chain was last at `regressedFrom`
}

/** The three chains, parsed API-side out of the Jobs table's status JSON. */
export interface JobStatusState {
	data: JobStatusTrack;
	logistics: JobStatusTrack;
	billing: JobStatusTrack;
}

export class Job extends BaseDataModel {
	readonly name!: string; // formula
	readonly edits!: string; // formula

	readonly status!: string; // formula
	readonly statusNumber!: JobStatusNumber; // formula

	// --- The new status system (written by central-nervous-system) ------------------------
	// Separate from `status`/`statusNumber` above, which are the long-standing
	// "Job Status - Simple"/"Job Status #" formulas. Surfaced developer-only (the panel's
	// Status section) until it ships; nothing else derives from these.
	readonly statusInternal?: string; // the calculated roll-up
	readonly statusCustomer?: string; // its customer-facing simplification
	// Undefined when the job has never been through the calculator — the common case today,
	// since it only backfills jobs created from 2026-08-01 — AND on jobs that reached the
	// panel from a lite list payload, which excludes the bulky source field (the dashboard
	// is the one such host).
	readonly statusState?: JobStatusState;
	sampleChanges!: string;
	boundaryAcres!: number;
	readonly opsAcres?: number; // "Ops Acre Eq." formula (area stats)
	readonly daysLate?: number; // "Late (days)" formula (area stats/heatmap)
	readonly grid?: number; // "Grid" lookup: sampling grid size in acres (unassigned-list stats)
	lastCrop?: string;
	nextCrop?: string;
	yieldGoal!: string;
	numberOfSamples?: number;

	fieldId?: string;
	readonly fieldName!: string; // GET: formula
	eventId!: string;

	jobFlags!: JobFlags[];
	dataSent!: string[];
	csvSentToLab!: boolean;
	/** Airtable "Billable Percent %": a 0–1 decimal the acres are multiplied by when the job is priced. */
	billablePercent?: number;
	boundaryChangeType?: BoundaryChangeType;
	boundaryChangeNotes?: string;
	// Inline boundary GeoJSON (Airtable "Bnd GeoJSON Text"). Parsed client-side with no fetch —
	// the scheduling boundary loader reads this first, falling back to the boundaryGeoJSON/boundarySHP
	// attachments. Only shipped by the scheduling boundary stream (excluded from lite list payloads).
	readonly boundaryGeoJsonText?: string;
	fieldPriority!: JobsPriority;

	readonly testPackage!: string; // lookup from Deal
	readonly season!: string; // lookup from Deal
	readonly depth!: number; // lookup from Deal
	readonly density?: number; // lookup from Deal
	readonly frequencyOfMicros?: number; // lookup from Deal
	readonly labName?: string; // lookup from Deal
	microsList?: string;
	isBillingApproved!: boolean;
	isMarkedReady!: boolean;
	isBoundaryChanged!: boolean;
	isBoundaryApproved!: boolean;
	isPointsChanged!: boolean;
	isTestJob?: boolean;
	isPointsAttached!: boolean;
	// Import-transient toggle: set in the import UI, translated to `dateMarkedReady`
	// on submit (see applyReadyAtSubmission). Not returned by the API — the
	// "Field Ready at Submission?" field it used to map to is being retired.
	isReadyToSample?: boolean;

	dateCreated!: Date;
	dateFileLastEdited?: Date;
	dateSampled?: Date;
	dateSampledManualOverride?: Date;
	dateShipped?: Date;
	dateLabResultsSent?: Date;
	dateFieldReadyEstimated?: Date;
	dateMarkedReady?: Date;

	submissionNotes?: string;
	// Airtable "Mgr Notes #ops" — the ops manager's free text on the job. Not skips-specific
	// (the approve-acres/billing views use the same field), but the Skips queue edits it.
	mgrNotes?: string;
	submitterEmail?: string;
	submitterName?: string;

	labSubmittalId!: string;
	readonly labAccountNumber?: string;
	labSubmissionCode?: string;
	labInstructions!: string;

	// Ops-curated SHP column hint. For customers whose Points SHPs use a non-standard sample-id
	// column (e.g. "sp_number" instead of one of the names in shared-ts ID_OPTIONS), this field
	// tells `Mission.load` which property to read as the sample id. Mirrors exit-interview-lambda's
	// `this.job.selectedSampleColumn` → `Mission.load(..., { selectedIdColumn })`. Without it, the
	// parser silently drops every point because it can't find a matching id column, and the diff
	// section ends up showing zero submitted samples.
	readonly selectedSampleColumn?: string;

	// Null on lite list payloads (which omit attachments to keep the fetch fast
	// and the payload small) until populated by `jobsList.ensureAttachments`.
	// {} means "loaded, no files". Guard reads with `?.` accordingly.
	attachments!: {
		pointsSHP?: IAirtableAttachment[];
		boundarySHP?: IAirtableAttachment[];
		boundaryGeoJSON?: IAirtableAttachment[];
		zonesSHP?: IAirtableAttachment[];
		executedPointsSHP?: IAirtableAttachment[];
		executedBoundarySHP?: IAirtableAttachment[];
		executedPointsBoundarySHP?: IAirtableAttachment[];
		labPDF?: IAirtableAttachment[];
		labCSV?: IAirtableAttachment[];
		labResults?: IAirtableAttachment[];
		fieldNotes?: IAirtableAttachment[];
		exeMission?: IAirtableAttachment[];
		intMission?: IAirtableAttachment[];
	} | null;

	/**
	 * Contains the file data itself. Primarily intended for creating new jobs.
	 */
	files!: {
		pointsSHP?: File[];
		pointsGeoJSON?: FeatureCollection<Geometry, GeoJsonProperties>;
		boundarySHP?: File[];
		boundaryGeoJSON?: FeatureCollection<Geometry, GeoJsonProperties>;
		zonesSHP?: File[];
		zonesGeoJSON?: FeatureCollection<Geometry, GeoJsonProperties>;
		executedPointsSHP?: File[];
		executedBoundarySHP?: File[];
		executedPointsBoundarySHP?: File[];
		labPDF?: File[];
		labCSV?: File[];
		labResults?: File[];
		fieldNotes?: File[];
	};

	readonly companyName!: string; // lookup from Company
	readonly farmName?: string; // formula
	readonly growerName!: string; // lookup from Grower
	growerPhoneNumber?: string;

	branchIds!: string[];
	regionIds!: string[];
	dealIds!: string[];
	growerIds!: string[];
	salespersonIds!: string[];
	shiftId?: string;
	/** Run-sequence within the assigned shift (Airtable "Order"); persisted on scheduling save. */
	order?: number;
	/** Pull-in point, "lat,lng" (Airtable "Location") — the route-line stop for this job. */
	location?: string;
	/** Which writer produced `location` (Airtable "Lat Lon Source"). `CenterBoundary` is the
	 *  lambda's boundary centroid at intake; `Pullin` is a dispatcher's cluster pull-in from
	 *  scheduling, or a mapmaker's KML point; `NearestRoad` is that point snapped to a road.
	 *  Read-only here except through the scheduling save, which writes `Pullin`. */
	latLonSource?: LatLonSource;
	/** GeoJSON `FeatureCollection` text of map features attached outside mapmaking (Airtable
	 *  "Map Features GeoJSON (code)") — today the dispatcher's pull-in zones (`properties.name` =
	 *  "pullin"). Parse with `MapFeatures.parse`. Written only through the scheduling save's
	 *  `applyMapFeatureEdits` (server-side merge), never through `updateOne`. */
	mapFeaturesGeoJson?: string;
	/** Shift Dropoff receiving this job's samples (Airtable "Dropoffs/Shipping"); tagged on save. */
	dropoffShippingIds?: string[];

	apiId?: string; // also used for creating new jobs
	softwareErrors!: ErrorNote[];
	softwareNotes!: ErrorNote[];
	// Raw text field, one milestone per line ("<ISO8601 timestamp>::<name>"). Exposed unparsed so
	// the frontend can parse it with the `parseMilestones` operation — see
	// `@domain/MissionControl` operations/milestones + operations/softwareMilestoneEvents.
	softwareMilestones!: string;
	url!: string;

	// used for creating new jobs
	importFieldName?: string;
	importDepth?: number;
	importDensity?: number;
	importLabName?: string;
	importFarmName?: string;
	importGrowerName?: string;
	importBranchName?: string;

	externalId?: string;
	externalTestPackageId?: string;
	externalFrequencyOfMicros?: number;
	externalLabId?: string;
	externalFarmId?: string;
	thirdPartyCustomerIdForDealSearch?: string;
	thirdPartyLocationId?: string;
	labCode?: string;
	externalGrower?: {
		id: string;
		name: string;
	};
	newSalesperson?: {
		name: string;
		phone: string;
	};
	collectBoundary?: boolean;

	// Skip Management — populated on jobs that went through the skips workflow; absent on normal jobs.
	exeSamples?: number; // samples in the exe file: numberOfSamples plus any the operator added
	exeSamplesSkipped?: number;
	skipsInExe?: string; // "None" | "Permanent Skip" | "Temporary Skip"
	pulledPercentSkipMgmt?: number; // 0–1 decimal (multiply by 100 for display)
	previouslySampledExeSkipMgmt?: number;
	contJobIds!: string[]; // continuation job (set on the archive job)
	archiveJobIds!: string[]; // archive job (set on the continuation job)

	/** Pass-through to {@link defaultJobForImport} in `../operations/defaultJobForImport`. */
	static defaultForImport(defaults: JobImportDefaults = {}): Job {
		return defaultJobForImport(defaults);
	}

	/** Pass-through to {@link updateJobFromDeal} in `../operations/updateJobFromDeal`. */
	static updateFromDeal(job: Job, deal: Deal): void {
		updateJobFromDeal(job, deal);
	}
}
